import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  reduxForm,
  Field,
  FieldArray,
  arrayRemove,
  arraySwap,
  change,
} from 'redux-form';
import _ from 'lodash';
import { Popup, Icon, Button } from 'semantic-ui-react';

import { InputField, Select, PopupField } from 'Common/SemanticWrapper';
import {
  IngredientFieldEnum as IngredientFields,
  RecipeFieldEnum as RecipeFields,
  PreparationInstructionEnum,
} from 'Common/FieldEnum';
import ReactTable from 'Common/ReactTableWrapper';
import { InputMode, EDIT_RECIPE_FORM_NAME } from 'Common/FormEnum';
import {
  ReactTableWrapperColumnProperties,
  ReactTableWrapperCogColumnProperties,
  NOTIFICATION_DIALOG_MESSAGES,
} from 'Common/Constants';
import { setNotificationDialog } from 'Components/dialogs/state/actions';
import { GridContextMenu } from 'Components/app/contextmenu/ContextMenuManager';
import { balance } from 'Common/Helpers/format';
import { makeUnitsOptions, makeUnitsOptionsWithNull } from 'Common/FormHelper';

class IngTable extends React.Component {
  handleViewEditIngredient = (row, index) => {
    const { openIngredientModal, formValues } = this.props;
    const ingredientId = _.get(
      formValues,
      `${RecipeFields.Ingredients}[${index}].${IngredientFields.Id}`
    );
    if (ingredientId) {
      openIngredientModal(ingredientId);
    }
  };
  handleDeleteIngredient = (row) => {
    const { dispatch, formValues } = this.props;
    const path = _.toPath(row);

    const index = Number(path[1]);

    const instructions = _.get(
      formValues,
      [RecipeFields.PreparationInstructions],
      []
    );

    const removedIngIndex = index + 1;

    const instructionsWithoutRemovedIngredient = instructions.map(
      (instruction) => {
        const ingIndexes =
          instruction[PreparationInstructionEnum.Ingredients] || [];
        return {
          ...instruction,
          [PreparationInstructionEnum.Ingredients]: ingIndexes.filter(
            (ingIndex) => ingIndex !== removedIngIndex
          ),
        };
      }
    );

    dispatch(
      setNotificationDialog({
        ...NOTIFICATION_DIALOG_MESSAGES.DELETE_INGREDIENT_FROM_RECIPE,
        actionsToDispatchOnOk: [
          arrayRemove(EDIT_RECIPE_FORM_NAME, RecipeFields.Ingredients, index),
          change(
            EDIT_RECIPE_FORM_NAME,
            RecipeFields.PreparationInstructions,
            instructionsWithoutRemovedIngredient
          ),
        ],
      })
    );
  };

  handleSwapItemOrder = (indexA, indexB) => {
    this.props.dispatch(
      arraySwap(EDIT_RECIPE_FORM_NAME, RecipeFields.Ingredients, indexA, indexB)
    );
  };

  menuOptions = [
    {
      icon: 'pencil',
      text: 'View/Edit',
      handler: this.handleViewEditIngredient,
      disabledWhen: (x) => this.props.isReadOnly,
    },
    {
      icon: 'trash',
      text: 'Delete',
      className: 'danger',
      handler: this.handleDeleteIngredient,
      disabledWhen: (x) => this.props.isReadOnly,
    },
  ];

  /** Creates accessor to grab the row value for field, used for filter */
  createAccessor = (field) => (row) => {
    const { fields } = this.props;
    const path = _.toPath(row);
    const index = Number(path[1]);
    const ing = fields.get(index);

    return _.get(ing, field, '');
  };

  columns = [
    {
      id: 'CN_Code',
      Header: 'Ingredient #',
      className: 'td ingredient-id-container',
      ...ReactTableWrapperColumnProperties,
      sortable: false,
      width: 120,
      accessor: this.createAccessor(IngredientFields.CN_Code),
      Cell: (row) => {
        const { fields } = this.props;
        const ing = fields.get(row.index);
        const style = {};
        if (_.get(ing, [IngredientFields.Inactive]) === true) {
          style.textDecoration = 'line-through';
        }

        return (
          <Field
            component={PopupField}
            name={`${row.original}.${IngredientFields.CN_Code}`}
            style={style}
          />
        );
      },
    },
    {
      id: 'IngredientName',
      Header: 'Ingredient Name',
      className: 'td',
      ...ReactTableWrapperColumnProperties,
      sortable: false,
      accessor: this.createAccessor(IngredientFields.IngredientName),
      Cell: (row) => {
        const { fields } = this.props;
        const ing = fields.get(row.index);
        const style = {};
        if (_.get(ing, [RecipeFields.Inactive]) === true) {
          style.textDecoration = 'line-through';
        }

        return (
          <Field
            component={PopupField}
            name={`${row.original}.${IngredientFields.IngredientName}`}
            style={style}
          />
        );
      },
    },
    {
      id: 'Quantity1',
      Header: 'Quantity',
      className: 'td field-container',
      width: 90,
      ...ReactTableWrapperColumnProperties,
      sortable: false,
      accessor: this.createAccessor(IngredientFields.Quantity1),
      Cell: (row) => {
        const { isReadOnly } = this.props;
        return (
          <Field
            component={InputField}
            mode={InputMode.Decimal}
            numericSize={[12, 4]}
            name={`${row.original}.${IngredientFields.Quantity1}`}
            hideEmptyError={true}
            label={false}
            maxLength="6"
            showOnlyTouchedError={true}
            disabled={isReadOnly}
          />
        );
      },
    },
    {
      id: 'Measure1',
      Header: 'Measure',
      className: 'td field-container',
      width: 120,
      ...ReactTableWrapperColumnProperties,
      sortable: false,
      accessor: this.createAccessor(IngredientFields.Measure1),
      Cell: (row) => {
        const { fields, isReadOnly } = this.props;
        const ing = fields.get(row.index);
        return (
          <Field
            component={Select}
            usePortal
            name={`${row.original}.${IngredientFields.Measure1}`}
            label={''}
            fluid
            options={makeUnitsOptions(ing[IngredientFields.Measures])}
            disabled={isReadOnly}
          />
        );
      },
    },
    {
      id: 'Quantity2',
      Header: 'Quantity',
      width: 90,
      className: 'td field-container',
      ...ReactTableWrapperColumnProperties,
      sortable: false,
      accessor: this.createAccessor(IngredientFields.Quantity2),
      Cell: (row) => {
        const { isReadOnly } = this.props;
        return (
          <Field
            component={InputField}
            mode={InputMode.Decimal}
            numericSize={[12, 4]}
            name={`${row.original}.${IngredientFields.Quantity2}`}
            hideEmptyError={true}
            label={false}
            maxLength="6"
            showOnlyTouchedError={true}
            disabled={isReadOnly}
          />
        );
      },
    },
    {
      id: 'Measure2',
      Header: 'Measure',
      className: 'td field-container',
      width: 120,
      ...ReactTableWrapperColumnProperties,
      sortable: false,
      accessor: this.createAccessor(IngredientFields.Measure2),
      Cell: (row) => {
        const { fields, isReadOnly } = this.props;
        const ing = fields.get(row.index);
        return (
          <Field
            component={Select}
            usePortal
            name={`${row.original}.${IngredientFields.Measure2}`}
            label={''}
            fluid
            options={makeUnitsOptionsWithNull(ing[IngredientFields.Measures])}
            disabled={isReadOnly}
          />
        );
      },
    },
    {
      id: 'IsOnMenu',
      Header: (
        <Popup
          trigger={<Icon name={'utensils'} />}
          content={'Used On Menu'}
          on="hover"
        />
      ),
      width: 50,
      className: 'td checkmark-container',
      ...ReactTableWrapperColumnProperties,
      sortable: false,
      filterable: false,
      Cell: (row) => {
        const { fields } = this.props;
        const ing = fields.get(row.index);
        if (ing.IsOnMenu) {
          return <Icon name={'checkmark'} />;
        }
        return <div />;
      },
    },
    {
      id: 'AllergensVerified',
      Header: (
        <Popup
          trigger={<Icon name={'exclamation triangle'} />}
          content={'Allergens Verified'}
          on="hover"
        />
      ),
      width: 50,
      className: 'td checkmark-container',
      ...ReactTableWrapperColumnProperties,
      sortable: false,
      filterable: false,
      Cell: (row) => {
        const { fields } = this.props;
        const ing = fields.get(row.index);
        if (ing.AllergensVerified) {
          return <Icon name={'checkmark'} />;
        }
        return <div />;
      },
    },
    {
      id: 'HasAttributes',
      Header: (
        <Popup
          trigger={<Icon name={'leaf'} />}
          content={'Has Attributes'}
          on="hover"
        />
      ),
      width: 50,
      className: 'td checkmark-container',
      ...ReactTableWrapperColumnProperties,
      sortable: false,
      filterable: false,
      Cell: (row) => {
        const { fields } = this.props;
        const ing = fields.get(row.index);
        if (ing.HasAttributes) {
          return <Icon name={'checkmark'} />;
        }
        return <div />;
      },
    },
    {
      id: 'LinkedToInv',
      Header: (
        <Popup
          trigger={<Icon name={'linkify'} />}
          content={'Linked To Inventory'}
          on="hover"
        />
      ),
      width: 50,
      className: 'td checkmark-container',
      ...ReactTableWrapperColumnProperties,
      sortable: false,
      filterable: false,
      Cell: (row) => {
        const { fields } = this.props;
        const ing = fields.get(row.index);
        if (ing.IsLinkedToINV) {
          return <Icon name={'checkmark'} />;
        }
        return <div />;
      },
    },
    {
      id: 'Cost',
      Header: (
        <Popup
          trigger={<span>Cost</span>}
          content={'Cost Per Serving'}
          on="hover"
        />
      ),
      width: 100,
      className: 'td cost-per-serving-container',
      ...ReactTableWrapperColumnProperties,
      sortable: false,
      filterable: false,
      Cell: (row) => {
        const { fields } = this.props;
        const ing = fields.get(row.index);
        const style = {};
        if (_.get(row, [ing, IngredientFields.Inactive]) === true) {
          style.textDecoration = 'line-through';
        }

        let costPerServing = 'N/A';
        if (ing[IngredientFields.CostPerServing] !== null) {
          costPerServing = balance(ing[IngredientFields.CostPerServing]);
        }
        return <span style={style}>{costPerServing}</span>;
      },
    },
    {
      fixed: 'right',
      className: 'td cog-container',
      ...ReactTableWrapperCogColumnProperties,
      width: 125,
      Cell: (row) => {
        const { isReadOnly } = this.props;

        return (
          <React.Fragment>
            <span>
              <Button
                compact
                icon="angle up"
                disabled={row.index === 0 || isReadOnly}
                onClick={() =>
                  this.handleSwapItemOrder(row.index, row.index - 1)
                }
              />
              <Button
                compact
                icon="angle down"
                disabled={row.index === row.pageSize - 1 || isReadOnly}
                onClick={() =>
                  this.handleSwapItemOrder(row.index, row.index + 1)
                }
              />
            </span>
            {GridContextMenu({
              gridProps: row,
              options: this.menuOptions,
            })}
          </React.Fragment>
        );
      },
    },
  ];

  getTrProps = (state, rowInfo, column) => {
    const { fields } = this.props;
    const style = {};

    if (rowInfo) {
      const ing = fields.get(rowInfo.index);

      const isInactive = _.get(ing, [RecipeFields.Inactive]) === true;
      const isUsedOnMenu = _.get(ing, [RecipeFields.UsedOnMenu]) === true;

      if (isUsedOnMenu) {
        style.color = 'blue';
      }

      if (isInactive) {
        style.color = 'red';
      }
    }

    return {
      style,
    };
  };

  render() {
    const { fields, loading } = this.props;

    return (
      <ReactTable
        data={fields}
        pageSize={fields.length}
        showPageSizeOptions={false}
        showPagination={false}
        className="-striped -highlight"
        loading={loading}
        columns={this.columns}
        draggableColumns={false}
        showHideColumn={false}
        menu="hasCogs"
        getTrProps={this.getTrProps}
        showMenu={false}
        resetPageOnDataChange={false}
        sortable={false}
      />
    );
  }
}

IngTable = connect()(IngTable);

class RecipeIngredientTable extends Component {
  static propTypes = {
    loading: PropTypes.bool,
  };

  static defaultProps = {
    loading: false,
  };

  render() {
    const { isReadOnly, formValues, loading, openIngredientModal } = this.props;

    return (
      <FieldArray
        name={RecipeFields.Ingredients}
        component={IngTable}
        isReadOnly={isReadOnly}
        formValues={formValues}
        loading={loading}
        openIngredientModal={openIngredientModal}
      />
    );
  }
}

RecipeIngredientTable = reduxForm({
  form: EDIT_RECIPE_FORM_NAME,
  enableReinitialize: true,
  destroyOnUnmount: false,
})(RecipeIngredientTable);

export { RecipeIngredientTable };



// WEBPACK FOOTER //
// ./src/pages/managerecipe/components/RecipeIngredientTable.js